import { ChevronDown, ChevronRight } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DateTimePicker } from '@nucleus-commerce/admin-kit/date-time';
import { useApplyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout, FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import {
  apiFieldToFormField,
  blockFormSchema,
  contentStatuses,
  type BlockFormInput,
  type BlockFormOutput,
} from '../../schemas/block-form-schema';
import { BlockKindBadge } from '../content-badges';
import { CustomFieldsEditor } from '../custom-fields-editor';

import { BlockDetailsFields } from './block-details-fields';

const BLOCK_FORM_ID = 'content-block-form';

export interface BlockEditorShellProps {
  mode: 'create' | 'edit';
  /** Its `kind` decides which editor the children are; the shell only validates the union. */
  defaultValues: BlockFormInput;
  isSubmitting: boolean;
  onSubmit: (values: BlockFormOutput) => Promise<unknown>;
  /** The kind's own sections, rendered between Details and Advanced. */
  children: ReactNode;
  /** Slot under the save card for the live preview panel (BlockEditor fills it). */
  preview?: ReactNode;
  /** Extra side cards under the schedule. */
  aside?: ReactNode;
  dangerZone?: ReactNode;
  /** Which guided tour points into this editor: a store block's or a shared block's. */
  tour?: 'store' | 'shared';
}

/** The custom bag, collapsed by default: most blocks never need it. */
function AdvancedSection() {
  const { t } = useTranslation('content');
  const [open, setOpen] = useState(false);
  const panelId = useId();
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <CardTitle>{t('sections.advanced')}</CardTitle>
          <CardDescription>{t('sections.customDescription')}</CardDescription>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((value) => !value)}
        >
          {open ? <ChevronDown aria-hidden /> : <ChevronRight aria-hidden />}
          {open ? t('sections.advancedHide') : t('sections.advancedShow')}
        </Button>
      </CardHeader>
      {open ? (
        <CardContent id={panelId} className="flex flex-col gap-4">
          <CustomFieldsEditor<BlockFormInput> name="custom" label={t('sections.custom')} />
        </CardContent>
      ) : null}
    </Card>
  );
}

/**
 * What every block editor shares: the form context validated by the kind union, details and
 * advanced sections around the kind's own body, and a wide sticky aside with the save card,
 * the preview slot, the schedule, extra cards and the danger zone. The contextual save bar
 * owns the unsaved state: Save and Discard up top, and no leaving until one of them happened.
 */
export function BlockEditorShell({
  mode,
  defaultValues,
  isSubmitting,
  onSubmit,
  children,
  preview,
  aside,
  dangerZone,
  tour,
}: BlockEditorShellProps) {
  const { t } = useTranslation(['content', 'common']);
  const storeTour = tour === 'store';
  const sharedTour = tour === 'shared';
  const message = useErrorMessage();
  const form = useForm<BlockFormInput, unknown, BlockFormOutput>({
    resolver: useI18nResolver(blockFormSchema),
    defaultValues,
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError, apiFieldToFormField);
  const isDirty = form.formState.isDirty;
  const rootError = form.formState.errors.root?.server?.message;

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await onSubmit(values);
      if (mode === 'edit') form.reset(values);
    } catch (error) {
      if (!applyFieldErrors(error)) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
    }
  });

  useSaveBar({ form, onSave: submit, saving: isSubmitting });

  return (
    <Form {...form}>
      <FormLayout
        asideWidth="wide"
        aside={
          <div className="flex flex-col gap-6 xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] xl:overflow-y-auto">
            <FormSection
              title={t('content:sections.status')}
              tour={
                storeTour
                  ? 'content.block-editor.save'
                  : sharedTour
                    ? 'shared-blocks.editor.save'
                    : undefined
              }
            >
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">{t('content:fields.kind')}</span>
                <BlockKindBadge kind={defaultValues.kind} />
              </div>
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="gap-2.5"
                      >
                        {contentStatuses.map((status) => (
                          <div key={status} className="flex items-start gap-2.5">
                            <RadioGroupItem
                              value={status}
                              id={`block-status-${status}`}
                              className="mt-0.5"
                            />
                            <div className="grid gap-0.5">
                              <Label htmlFor={`block-status-${status}`}>
                                {t(`content:status.${status}`)}
                              </Label>
                              <span className="text-muted-foreground text-xs">
                                {t(`content:statusHint.block.${status}`)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Can permission="content.write">
                <div className="flex items-center gap-2 border-t pt-4">
                  <Button
                    type="submit"
                    form={BLOCK_FORM_ID}
                    disabled={isSubmitting || (mode === 'edit' && !isDirty)}
                    className="flex-1"
                  >
                    {isSubmitting
                      ? t('common:actions.saving')
                      : mode === 'create'
                        ? t('content:block.create')
                        : t('common:actions.save')}
                  </Button>
                  {mode === 'edit' ? (
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={isSubmitting || !isDirty}
                      onClick={() => form.reset()}
                    >
                      {t('content:actions.discard')}
                    </Button>
                  ) : null}
                </div>
              </Can>
            </FormSection>
            <div
              className="grid"
              data-tour={
                storeTour
                  ? 'content.block-editor.preview'
                  : sharedTour
                    ? 'shared-blocks.editor.preview'
                    : undefined
              }
            >
              {preview}
            </div>
            <FormSection
              title={t('content:sections.schedule')}
              description={t('content:sections.scheduleDescription')}
              tour={
                storeTour
                  ? 'content.block-editor.schedule'
                  : sharedTour
                    ? 'shared-blocks.editor.schedule'
                    : undefined
              }
            >
              <FormField
                control={form.control}
                name="startsAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('content:fields.startsAt')}</FormLabel>
                    <FormControl>
                      <DateTimePicker {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="endsAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('content:fields.endsAt')}</FormLabel>
                    <FormControl>
                      <DateTimePicker {...field} />
                    </FormControl>
                    <FormDescription>{t('content:fields.scheduleHint')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>
            {aside}
            {dangerZone}
          </div>
        }
      >
        <form id={BLOCK_FORM_ID} onSubmit={submit} noValidate className="flex flex-col gap-6">
          {rootError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          ) : null}
          <div
            className="grid"
            data-tour={
              storeTour
                ? 'content.block-editor.details'
                : sharedTour
                  ? 'shared-blocks.editor.details'
                  : undefined
            }
          >
            <BlockDetailsFields mode={mode} />
          </div>
          <div
            className="flex flex-col gap-6"
            data-tour={
              !storeTour
                ? undefined
                : defaultValues.kind === 'banner'
                  ? 'content.block-editor.slides'
                  : 'content.block-editor.rail-source'
            }
          >
            {children}
          </div>
          <AdvancedSection />
        </form>
      </FormLayout>
    </Form>
  );
}
